import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listen, UnlistenFn } from '@tauri-apps/api/event'
import { invoke } from '@tauri-apps/api/core'
import { useAuthStore } from './store'
import {
  generateCasdoorSignInUrl,
  handleCasdoorCallback,
  startRustOAuthLogin,
  handleRustOAuthCallback,
  OAuthCallbackResult
} from './casdoor'
import { CASDOOR_REDIRECT_URI, markWebLoginFlag, clearWebLoginFlag } from './casdoor'
import { tauriApi } from '../api/tauriApi'
import { isWebPlatform } from '../utils/platform'
import { isHarmonyOS } from '../utils/mobile'

// 登录 hook
export function useLogin() {
  const navigate = useNavigate()
  const { setLoading, setError, isLoading } = useAuthStore()
  const isLoggingInRef = useRef(false)

  const login = useCallback(async () => {
    if (isLoggingInRef.current) {
      console.log('[useLogin] 登录已在进行中，跳过重复调用')
      return
    }

    isLoggingInRef.current = true

    try {
      setLoading(true)
      setError(null)

      console.log('[useLogin] 开始 OAuth 登录流程')

      const result = await startRustOAuthLogin()

      if (!result.success) {
        throw new Error(result.error || '启动 OAuth 登录失败')
      }

      console.log('[useLogin] Rust OAuth 登录已启动')
      setLoading(false)
    } catch (error) {
      console.error('[useLogin] 登录失败:', error)
      setError(error instanceof Error ? error.message : '登录失败')
      setLoading(false)
    } finally {
    // 不重置 isLoggingInRef：startRustOAuthLogin 成功后页面即将离开，
    // 重置锁会导致 React StrictMode 双渲染时重复调用 oauth_login。
    // 登录失败时 isNavigatingToCasdoor 已在 casdoor.ts catch 中重置，
    // 用户点击重试按钮会创建新的组件实例和 login() 调用。
  }
  }, [setLoading, setError])

  return { login }
}

// 默认用户 ID（未登录状态）
const DEFAULT_USER_ID = 'default-user'

// 登出 hook
export function useLogout() {
  const navigate = useNavigate()
  const { logout, accessToken, refreshToken } = useAuthStore()

  const handleLogout = useCallback(async () => {
    console.log('[useLogout] 开始登出流程')
    try {
      // 1. 调用 Rust 后端撤销 token
      try {
        const { invoke } = await import('@tauri-apps/api/core')
        await invoke('oauth_logout', {
          accessToken: accessToken || null,
          refreshToken: refreshToken || null,
        })
        console.log('[useLogout] Token 撤销完成')
      } catch (revokeError) {
        console.error('[useLogout] Token 撤销失败:', revokeError)
        // 继续登出流程
      }

      // 2. 清理 Electric 集合（保留 SQLite 缓存，用户切换由 doInitializeCollections 检测处理）
      try {
        const { cleanupCollections } = await import('../store/collections')
        await cleanupCollections({ clearCache: false })
        console.log('[useLogout] Electric 集合已清理（SQLite 缓存保留）')
      } catch (cleanupError) {
        console.error('[useLogout] 集合清理失败:', cleanupError)
      }

      // 2.5 清理 SubscriptionService 内存缓存（订阅/用户信息 TTL 缓存按用户隔离，
      // 登出不清会导致下一个登录用户在 60s 内读到上一个用户的订阅状态）
      try {
        const { subscriptionService } = await import('../services/SubscriptionService')
        subscriptionService.clearSubscriptionCache()
        console.log('[useLogout] 订阅缓存已清理')
      } catch (cacheError) {
        console.warn('[useLogout] 清理订阅缓存失败:', cacheError)
      }

      // 3. 清理 noteGroups 和 sharedPrompts 集合
      try {
        const { cleanup: cleanupNoteGroups } = await import('../store/collections/noteGroupsCollection')
        cleanupNoteGroups()
      } catch {}
      try {
        const { cleanup: cleanupSharedPrompts } = await import('../store/collections/sharedPromptsCollection')
        cleanupSharedPrompts()
      } catch {}
      // 清理跨设备头像同步集合（登出/切换用户）
      try {
        const { cleanup: cleanupAvatarSync } = await import('../store/collections/avatarSyncCollection')
        cleanupAvatarSync()
      } catch {}
      // 清理跨设备锁密码同步集合（登出/切换用户）
      try {
        const { cleanup: cleanupLockSettings } = await import('../store/collections/lockSettingsCollection')
        cleanupLockSettings()
      } catch {}

      // 4. 重置本地数据库用户为默认用户
      try {
        await tauriApi.setCurrentUser(DEFAULT_USER_ID)
        console.log('[useLogout] 本地数据库用户已重置为默认用户')
      } catch (resetError) {
        console.error('[useLogout] 重置本地数据库用户失败:', resetError)
        // 继续登出流程
      }

      // 5. 清除本地状态（立即生效）- 现在 logout 是异步的
      console.log('[useLogout] 清除本地登录状态')
      await logout()

      // 6. 在 localStorage 中标记刚刚登出（用于防止自动登录）
      localStorage.setItem('just_logged_out', 'true')
      // 6.5 清除跨子域登录标记（Web 端：让 www 官网恢复展示官网而非跳转）
      clearWebLoginFlag()
      // 清除本地缓存 userId 标记（防止下次冷启动乐观放行到已登出用户）
      localStorage.removeItem('modern_notes_user_id')
      console.log('[useLogout] 已设置 just_logged_out 标记')

      // 7. 跳转到登录页面
      console.log('[useLogout] 跳转到登录页面')
      navigate('/login?from=logout', { replace: true })
    } catch (error) {
      console.error('[useLogout] 登出失败:', error)
      // 即使出错也尝试跳转到登录页
      navigate('/login?from=logout', { replace: true })
    }
  }, [logout, navigate, accessToken, refreshToken])

  return { logout: handleLogout }
}

// 会话恢复 hook
export function useRestoreSession() {
  const { restoreSession } = useAuthStore()
  const [isRestoring, setIsRestoring] = useState(true)
  const [restoreError, setRestoreError] = useState<string | null>(null)

  useEffect(() => {
    const restore = async () => {
      try {
        console.log('[useRestoreSession] 尝试恢复会话')

        // 检查是否刚刚登出
        const justLoggedOut = localStorage.getItem('just_logged_out')
        if (justLoggedOut === 'true') {
          console.log('[useRestoreSession] 刚刚登出，跳过会话恢复')
          localStorage.removeItem('just_logged_out')
          // 关键：同步 zustand 的 isRestoring（ProtectedRoute 读取的是 zustand 状态）
          useAuthStore.getState().setRestoring(false)
          setIsRestoring(false)
          return
        }

        const restored = await restoreSession()
        if (restored) {
          console.log('[useRestoreSession] 会话恢复成功')
          // Web 端：会话恢复成功即视为已登录，刷新跨子域标记（续期 30 天）
          markWebLoginFlag()
        } else {
          console.log('[useRestoreSession] 没有可恢复的会话')
        }
      } catch (error) {
        console.error('[useRestoreSession] 恢复会话失败:', error)
        setRestoreError(error instanceof Error ? error.message : '恢复会话失败')
      } finally {
        setIsRestoring(false)
      }
    }

    // 安全兜底：18s 后强制 isRestoring: false（覆盖所有未预见的挂起路径）
    // 防御性设计：即使 restoreSession 内部 15s 超时因某种原因未生效，
    // 此处也能确保 ProtectedRoute 不会永久 spinner
    const safetyTimer = setTimeout(() => {
      useAuthStore.getState().setRestoring(false)
      setIsRestoring(false)
      console.warn('[useRestoreSession] 安全兜底：18s 超时强制完成')
    }, 18000)

    restore().finally(() => clearTimeout(safetyTimer))

    return () => clearTimeout(safetyTimer)
  }, [restoreSession])

  return { isRestoring, restoreError }
}

// 处理回调 hook
export function useHandleCallback() {
  const navigate = useNavigate()
  const { setLoading, setError, setTokens, setUser } = useAuthStore()

  const handleCallback = useCallback(async (code: string, state: string) => {
    try {
      setLoading(true)
      setError(null)

      // 1. 验证 state 并获取 codeVerifier
      const casdoorResult = await handleCasdoorCallback(code, state)
      console.log('[useHandleCallback] State 验证成功')
      console.log('[useHandleCallback] codeVerifier:', casdoorResult.codeVerifier ? '存在' : '不存在')

      // 2. 调用 Rust 后端完成 OAuth 回调处理
      console.log('[useHandleCallback] 调用 Rust 后端完成登录')

      const redirectUri = localStorage.getItem('casdoor_redirect_uri') || CASDOOR_REDIRECT_URI

      const result = await handleRustOAuthCallback(
        casdoorResult.code,
        casdoorResult.state,
        redirectUri
      )

      localStorage.removeItem('casdoor_redirect_uri')

      if (!result.success) {
        throw new Error(result.error || '认证失败')
      }

      console.log('[useHandleCallback] Rust 后端认证成功:', result.user)

      // 初始化本地数据库用户上下文（数据隔离关键步骤）
      if (result.user) {
        try {
          await tauriApi.initUser(
            result.user.id,
            result.user.email,
            result.user.nickname || result.user.name || ''
          )
          console.log('[useHandleCallback] 本地数据库用户初始化成功:', result.user.id)
        } catch (initError) {
          console.error('[useHandleCallback] 本地数据库用户初始化失败:', initError)
          // 继续登录流程，但记录错误
        }

        // ★ 修复：必须先 setUser 再 setTokens——setTokens 内部读取 store 中的 user
        // 判断是否落盘（store.ts setTokens 的 if (user) 分支），先 setTokens 时
        // user 仍为 null → token 永不持久化 → 重启后被迫重新登录。
        // （useOAuthUrlCallback 主路径顺序本就正确，此路径为历史遗留修复）
        setUser(result.user)
        if (result.accessToken && result.expiresIn) {
          await setTokens(result.accessToken, result.refreshToken || '', result.expiresIn)
        }

        if (result.user?.avatar && result.user?.name && result.accessToken) {
          invoke('cache_avatar_locally', {
            avatarUrl: result.user.avatar,
            username: result.user.name,
            accessToken: result.accessToken,
          }).catch(err => console.warn('[useHandleCallback] 头像缓存失败:', err))
        }

        // 鸿蒙端：上报 Push Kit 推送 token（A3，fire-and-forget，失败不阻断）
        reportHarmonyPushToken()
      }

      // 清除 localStorage 中的 auth data
      localStorage.removeItem('casdoor_code_verifier')
      localStorage.removeItem('casdoor_auth_state')

      // Web 端：登录成功，写入跨子域登录标记（www 官网自动跳转 Web 端）
      markWebLoginFlag()

      // 交由 StartupRedirect 决策落地页（移动端/兜底 → /suji 速记模式）
      // 首启若写死 /notes 会被钉在空库欢迎页干等 Electric 全量快照
      navigate('/', { replace: true })
    } catch (error) {
      console.error('[useHandleCallback] 处理回调失败:', error)
      setError(error instanceof Error ? error.message : '登录失败')
      navigate('/login', { replace: true })
    } finally {
      setLoading(false)
    }
  }, [navigate, setLoading, setError, setTokens, setUser])

  return { handleCallback }
}

// Token 刷新 hook
export function useTokenRefresh() {
  const { refreshTokens, expiresAt, logout } = useAuthStore()

  useEffect(() => {
    if (!expiresAt) return

    // ★ 会话恢复/建立即上报鸿蒙推送 token（冷启动 + token 轮换覆盖，非鸿蒙端内部直接返回）
    reportHarmonyPushToken()

    // 计算刷新时间（过期前 5 分钟）
    const refreshTime = expiresAt - 5 * 60 * 1000
    const now = Date.now()
    const delay = refreshTime - now

    // 如果已经过期或即将过期，立即刷新
    if (delay <= 0) {
      console.log('[useTokenRefresh] Token 即将过期，立即刷新')
      refreshTokens()
      return
    }

    console.log(`[useTokenRefresh] 将在 ${Math.round(delay / 1000)} 秒后刷新 token`)

    // 设置定时器
    const timer = setTimeout(() => {
      console.log('[useTokenRefresh] 执行定时刷新')
      refreshTokens()
    }, delay)

    return () => clearTimeout(timer)
  }, [expiresAt, refreshTokens, logout])
}

// 检查认证状态 hook
export function useCheckAuth() {
  const { isAuthenticated, expiresAt, refreshTokens, logout } = useAuthStore()

  const checkAuth = useCallback(async () => {
    if (!isAuthenticated) {
      return false
    }

    // 检查 token 是否过期
    if (expiresAt && Date.now() >= expiresAt) {
      // Token 已过期，尝试刷新
      console.log('[useCheckAuth] Token 已过期，尝试刷新')
      const refreshed = await refreshTokens()
      return refreshed
    }

    return true
  }, [isAuthenticated, expiresAt, refreshTokens])

  return { checkAuth }
}

// OAuth 回调事件监听 hook
// 仅监听 deep-link 事件中的 oauth-cancelled 事件（用户取消登录）
// 回调处理逻辑已统一迁移到 useOAuthUrlCallback，避免多重处理器竞态
export function useOAuthCallbackListener() {
  const { setLoading, setError } = useAuthStore()
  const cancelledUnlistenRef = useRef<UnlistenFn | null>(null)
  const isProcessingRef = useRef(false)
  const beforeUnloadHandlerRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    const setupListeners = async () => {
      try {
        // ★ Web 在线版：oauth-cancelled 事件由 Rust 发出，浏览器环境无此通道
        if (isWebPlatform()) {
          console.log('[useOAuthCallbackListener] Web 环境，跳过 Tauri 事件监听')
          return
        }

        console.log('[useOAuthCallbackListener] 设置 OAuth 事件监听')

        // 监听 oauth-cancelled 事件（用户取消登录）
        cancelledUnlistenRef.current = await listen<{ reason: string }>('oauth-cancelled', (event) => {
          if (isProcessingRef.current) {
            console.log('[useOAuthCallbackListener] 正在处理回调，忽略取消事件')
            return
          }

          console.log('[useOAuthCallbackListener] 收到 OAuth 取消事件:', event.payload)
          setLoading(false)
          setError('登录已取消')
        })

        // 注册 beforeunload 清理：页面通过 window.location.href 导航时，
        // React cleanup 可能未完成，导致 Tauri event listener callback 变成孤儿。
        // beforeunload 确保在页面卸载前主动解除 Tauri 事件监听。
        beforeUnloadHandlerRef.current = () => {
          if (cancelledUnlistenRef.current) {
            console.log('[useOAuthCallbackListener] beforeunload 清理事件监听')
            cancelledUnlistenRef.current()
            cancelledUnlistenRef.current = null
          }
        }
        window.addEventListener('beforeunload', beforeUnloadHandlerRef.current)

        console.log('[useOAuthCallbackListener] OAuth 事件监听已设置')
      } catch (error) {
        console.error('[useOAuthCallbackListener] 设置事件监听失败:', error)
      }
    }

    setupListeners()

    return () => {
      if (cancelledUnlistenRef.current) {
        console.log('[useOAuthCallbackListener] 清理 OAuth 取消事件监听')
        cancelledUnlistenRef.current()
        cancelledUnlistenRef.current = null
      }
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current)
        beforeUnloadHandlerRef.current = null
      }
    }
  }, [])
}

/**
 * 基于 URL 查询参数的 OAuth 回调检测 Hook
 * 
 * 用于桌面端：登录后 Casdoor 重定向到 tauri://localhost/?code=...&state=...
 * Tauri protocol-asset 只能提供根路径（index.html），不支持 SPA 路由回退，
 * 因此需要在此处通过 URL 查询参数检测回调内容。
 */
export function useOAuthUrlCallback() {
  const navigate = useNavigate()
  const { setLoading, setError, setTokens, setUser } = useAuthStore()
  const isProcessingRef = useRef(false)

  const navigateRef = useRef(navigate)
  const setTokensRef = useRef(setTokens)
  const setUserRef = useRef(setUser)
  const setLoadingRef = useRef(setLoading)
  const setErrorRef = useRef(setError)

  useEffect(() => {
    navigateRef.current = navigate
    setTokensRef.current = setTokens
    setUserRef.current = setUser
    setLoadingRef.current = setLoading
    setErrorRef.current = setError
  }, [navigate, setTokens, setUser, setLoading, setError])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')

    if (!code || !state || isProcessingRef.current) return

    // 注意：不检查 window.location.pathname
    // HashRouter 下 pathname 是实际 HTTP 路径（Android 为 /callback），与 hash 路由无关
    // 桌面端 pathname 为 /，Android 端为 /callback，都应处理回调
    const processCallback = async () => {
      isProcessingRef.current = true
      // 设置全局回调处理锁，防止 ProtectedRoute/LoginPage 在回调处理期间触发新登录
      sessionStorage.setItem('oauth_callback_processing', 'true')
      try {
        console.log('[useOAuthUrlCallback] 检测到 URL 中的 OAuth 回调参数')
        setLoadingRef.current(true)
        setErrorRef.current(null)

        const redirectUri = localStorage.getItem('casdoor_redirect_uri') || CASDOOR_REDIRECT_URI

        const { handleRustOAuthCallback } = await import('./casdoor')
        const result = await handleRustOAuthCallback(code, state, redirectUri)

        // 回调成功后再清理 URL
        // 如果 IPC 失败，code/state 仍在 URL 中供后续重试
        window.history.replaceState({}, '', window.location.pathname)

        localStorage.removeItem('casdoor_redirect_uri')

        if (!result.success) {
          throw new Error(result.error || '认证失败')
        }

        console.log('[useOAuthUrlCallback] OAuth 成功，用户:', result.user?.id)

        if (result.user) {
          try {
            await tauriApi.initUser(
              result.user.id,
              result.user.email,
              result.user.nickname || result.user.name || ''
            )
            console.log('[useOAuthUrlCallback] 本地数据库用户初始化成功')
          } catch (initError) {
            console.error('[useOAuthUrlCallback] 本地数据库用户初始化失败:', initError)
          }

          setUserRef.current(result.user)

          if (result.accessToken && result.expiresIn) {
            await setTokensRef.current(result.accessToken, result.refreshToken || '', result.expiresIn)
          }

          // 登录成功点立即预热集合初始化（fire-and-forget）：
          // 跳过"App 挂载 → syncUser → ElectricSyncProvider"串行链，
          // Electric shape 订阅与页面导航并行（single-flight 保证与 useElectricSync 合并）
          import('@store/collections')
            .then(m => m.initializeCollections(result.user.id))
            .catch(e => console.warn('[useOAuthUrlCallback] 集合预热失败（useElectricSync 将接管）:', e))
        }

        // Web 端：登录成功，写入跨子域登录标记（www 官网自动跳转 Web 端）
        markWebLoginFlag()

        // 交由 StartupRedirect 决策落地页（移动端/兜底 → /suji 速记模式）
        navigateRef.current('/', { replace: true })
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : ''
        const isCodeUsed = errMsg.includes('authorization code has been used')
        const isIpcError = errMsg.includes('IPC custom protocol failed') ||
          errMsg.includes('postMessage') ||
          errMsg.includes('Load failed')
        const isNetworkError = errMsg.includes('NetworkError') ||
          errMsg.includes('Failed to fetch') ||
          errMsg.includes('Token request failed')
        const isStateError = errMsg.includes('Invalid state') ||
          errMsg.includes('state not found')

        console.error('[useOAuthUrlCallback] 处理回调失败:', {
          message: errMsg,
          isCodeUsed,
          isIpcError,
          isNetworkError,
          isStateError,
        })

        // IPC 错误特殊处理：URL 中 code/state 未清除（修改 1 保证），再做最终重试
        // Tauri IPC 初始化可能较慢，invokeWithRetry 的 3 秒窗口不够时，
        // 利用 URL 中保留的参数额外做一次长延迟重试
        if (isIpcError) {
          const retryParams = new URLSearchParams(window.location.search)
          const retryCode = retryParams.get('code')
          const retryState = retryParams.get('state')
          if (retryCode && retryState) {
            console.log('[useOAuthUrlCallback] IPC 错误，URL 中仍有参数，2000ms 后最终重试...')
            await new Promise(resolve => setTimeout(resolve, 2000))
            try {
              const { handleRustOAuthCallback } = await import('./casdoor')
              const retryRedirectUri = localStorage.getItem('casdoor_redirect_uri') || CASDOOR_REDIRECT_URI
              const retryResult = await handleRustOAuthCallback(retryCode, retryState, retryRedirectUri)
              if (retryResult.success && retryResult.user) {
                console.log('[useOAuthUrlCallback] 最终重试成功，用户:', retryResult.user.id)
                window.history.replaceState({}, '', window.location.pathname)
                localStorage.removeItem('casdoor_redirect_uri')
                try {
                  await tauriApi.initUser(retryResult.user.id, retryResult.user.email,
                    retryResult.user.nickname || retryResult.user.name || '')
                } catch (initError) {
                  console.error('[useOAuthUrlCallback] 本地数据库用户初始化失败:', initError)
                }
                setUserRef.current(retryResult.user)
                if (retryResult.accessToken && retryResult.expiresIn) {
                  await setTokensRef.current(retryResult.accessToken, retryResult.refreshToken || '', retryResult.expiresIn)
                }
                // 同主路径：预热集合初始化（fire-and-forget）
                import('@store/collections')
                  .then(m => m.initializeCollections(retryResult.user.id))
                  .catch(e => console.warn('[useOAuthUrlCallback] 集合预热失败（useElectricSync 将接管）:', e))
                // Web 端：重试登录成功，写入跨子域登录标记
                markWebLoginFlag()
                // 交由 StartupRedirect 决策落地页（移动端/兜底 → /suji 速记模式）
                navigateRef.current('/', { replace: true })
                return
              }
            } catch (retryError) {
              console.error('[useOAuthUrlCallback] 最终重试也失败了:', retryError)
            }
          }
        }

        try {
          const { useAuthStore } = await import('./store')
          const restored = await useAuthStore.getState().restoreSession()
          if (restored) {
            console.log('[useOAuthUrlCallback] 本地已存在有效 session，跳过错误处理')
            navigateRef.current('/notes', { replace: true })
            return
          }
        } catch (restoreError) {
          console.error('[useOAuthUrlCallback] session 恢复失败:', restoreError)
        }

        if (isCodeUsed) {
          console.log('[useOAuthUrlCallback] 授权码已使用且无有效 session，重定向到登录页')
          navigateRef.current('/login?error=session_expired', { replace: true })
        } else {
          setErrorRef.current(errMsg || '登录失败')
          navigateRef.current('/login?error=' + encodeURIComponent(errMsg || '认证失败'), { replace: true })
        }
      } finally {
        setLoadingRef.current(false)
        // 清除全局回调处理锁
        sessionStorage.removeItem('oauth_callback_processing')
        // 延迟重置 isProcessingRef：避免 React StrictMode 双重 effect 立即重复处理
        // 同一 code/state 的回调处理在 5 秒内不会被重复触发
        setTimeout(() => {
          isProcessingRef.current = false
        }, 5000)
      }
    }

    processCallback()
  }, [])
}

// Re-export profile-related hooks from the hooks directory
export { useAvatarUpload } from './hooks/useAvatarUpload'
export type {
  UseAvatarUploadOptions,
  UseAvatarUploadReturn,
} from './hooks/useAvatarUpload'

// useAvatarCrop 不在此处 re-export：其依赖 react-image-crop（重型库），
// 由 AvatarCropModal（已懒加载）通过 './hooks/useAvatarCrop' 直接导入，避免进入首屏 bundle

export { useUpdateNickname } from './hooks/useUpdateNickname'
export type {
  UseUpdateNicknameOptions,
  UseUpdateNicknameReturn,
} from './hooks/useUpdateNickname'

// ============================================
// 华为推送 token 上报（鸿蒙端 Push Kit，A3 上架合规）
// ============================================

/**
 * 鸿蒙端获取 Push Kit token 并上报服务端（POST /api/push/token，userId 唯一 upsert）。
 * 非鸿蒙端直接返回；任何失败仅告警不阻断（推送为增强能力）。
 * 调用时机：登录成功后（useHandleCallback）+ 会话恢复后
 * （useTokenRefresh 挂载即触发，覆盖冷启动与 token 轮换后的重上报）。
 */
export async function reportHarmonyPushToken(): Promise<void> {
  if (!isHarmonyOS()) {
    return
  }
  try {
    const token = await invoke<string>('push_get_token')
    if (!token) {
      console.warn('[Push] push_get_token 返回空，跳过上报')
      return
    }
    const accessToken = useAuthStore.getState().accessToken
    if (!accessToken) {
      console.warn('[Push] 无 accessToken，跳过推送 token 上报')
      return
    }
    const apiBase = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:4000'
    const resp = await fetch(`${apiBase}/api/push/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ token, platform: 'harmony' }),
    })
    if (!resp.ok) {
      console.warn('[Push] 推送 token 上报失败:', resp.status)
      return
    }
    console.log('[Push] 推送 token 上报成功')
  } catch (error) {
    console.warn('[Push] 推送 token 获取/上报失败（不阻断）:', error)
  }
}

// ============================================
// 邮箱密码直登（跨端登录，配合设置页"账号安全"）
// ============================================

/**
 * 邮箱密码登录（全平台可用）：
 * 1. POST /api/auth/login-password { email, password } → 服务端本地 bcrypt 校验
 *    + Casdoor password grant 换真 token（响应形状与 /auth/casdoor/callback 一致）
 * 2. 凭证来源：设置页"账号安全"保存的邮箱+密码（华为账号登录用户亦同）
 * 3. 会话落地与 useHandleCallback 对齐：initUser → setUser → setTokens → 跳转 '/'
 */
export function usePasswordLogin() {
  const navigate = useNavigate()
  const { setLoading, setError, setTokens, setUser } = useAuthStore()
  const isLoggingInRef = useRef(false)

  const passwordLogin = useCallback(async (email: string, password: string) => {
    if (isLoggingInRef.current) {
      console.log('[usePasswordLogin] 登录已在进行中，跳过重复调用')
      return
    }
    if (!email || !password) {
      setError('请输入邮箱和密码')
      return
    }

    isLoggingInRef.current = true
    try {
      setLoading(true)
      setError(null)
      console.log('[usePasswordLogin] 开始邮箱密码登录')

      const apiBase = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:4000'
      const response = await fetch(`${apiBase}/api/auth/login-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({} as any))
        throw new Error(errorData.message || errorData.error || `登录失败 (${response.status})`)
      }
      const result = await response.json()
      console.log('[usePasswordLogin] 后端登录成功, user:', result.user?.id)

      const user = {
        id: result.user.id,
        email: result.user.email ?? '',
        name: result.user.name ?? '',
        nickname: result.user.nickname ?? '',
        avatar: result.user.avatar ?? '',
      }
      try {
        await tauriApi.initUser(user.id, user.email, user.nickname || user.name || '')
        console.log('[usePasswordLogin] 本地数据库用户初始化成功:', user.id)
      } catch (initError) {
        console.error('[usePasswordLogin] 本地数据库用户初始化失败:', initError)
      }

      setUser(user)
      if (result.accessToken && result.expiresIn) {
        await setTokens(result.accessToken, result.refreshToken || '', result.expiresIn)
      }

      // 鸿蒙端：同步 ArkTS 会话 + 上报推送 token（与 useHandleCallback 对齐）
      if (isHarmonyOS()) {
        try {
          await invoke('sync_current_session', { accessToken: result.accessToken })
          console.log('[usePasswordLogin] ArkTS 会话同步成功')
        } catch (syncError) {
          console.warn('[usePasswordLogin] ArkTS 会话同步失败:', syncError)
        }
        reportHarmonyPushToken()
      }

      markWebLoginFlag()
      navigate('/', { replace: true })
    } catch (error) {
      console.error('[usePasswordLogin] 邮箱密码登录失败:', error)
      setError(error instanceof Error ? error.message : '登录失败')
    } finally {
      setLoading(false)
      isLoggingInRef.current = false
    }
  }, [navigate, setLoading, setError, setTokens, setUser])

  return { passwordLogin }
}
